import { logger } from '@/lib/logger';

/**
 * Module-level WebSocket pool for the public leaderboard feed.
 *
 * Why this exists:
 *   Before this pool, every player navigation to/from War Room, Live (Sweat)
 *   or the TV view triggered a fresh WebSocket handshake (~200 ms TLS + WS
 *   upgrade + first frame). Switching tabs back and forth — exactly what
 *   happens during a live round — left players with a visible "Connecting…"
 *   moment and dropped a few seconds of live broadcasts.
 *
 *   The pool keeps one WS open per `code`. Multiple components on the same
 *   pool share the same socket. When the last subscriber unmounts, we don't
 *   close immediately — instead we hold the connection open for a short
 *   grace period (10 s) so a quick back-navigation reuses it instantly.
 *
 * Public API (unchanged from the legacy hook semantics):
 *   subscribe(code, { fetchFn, isCompleted, onUpdate }): unsubscribe()
 *
 *   • The first subscriber for a given `code` triggers:
 *       1. A one-shot HTTP fetch (so we have data immediately)
 *       2. WS connect (skipped when `isCompleted: true`)
 *   • Every new subscriber gets `lastData` synchronously via `onUpdate(...)`
 *     during the subscribe call.
 *   • The latest subscriber's `fetchFn` is used for polling fallback —
 *     a caller whose `fetchFn` captured a stale closure will overwrite the
 *     stored ref on each subscribe, matching the legacy `fetchFnRef` behaviour.
 */

const GRACE_PERIOD_MS = 10_000;
// Reconnect backoff: starts at 1 s, doubles up to 30 s. Resets on every
// successful open. Identical to the legacy hook so behaviour under flaky
// networks is unchanged.
const RECONNECT_START_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const POLL_INTERVAL_MS = 60_000;

const pool = new Map(); // code -> Connection

function getWsUrl(code) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
  const host = backendUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${host}/api/public/room/${code}/ws`;
}

function startPolling(conn) {
  if (conn.pollTimer) return;
  conn.pollTimer = setInterval(async () => {
    try {
      const result = await conn.fetchFn?.();
      if (result) {
        conn.lastData = { ...(conn.lastData || {}), ...result };
        broadcast(conn, result);
      }
    } catch (e) { logger.warn('Leaderboard poll failed:', e?.message); }
  }, POLL_INTERVAL_MS);
}

function stopPolling(conn) {
  if (conn.pollTimer) {
    clearInterval(conn.pollTimer);
    conn.pollTimer = null;
  }
}

function broadcast(conn, payload) {
  for (const cb of conn.subscribers) {
    try { cb(payload); } catch (e) { logger.warn('Leaderboard subscriber threw:', e?.message); }
  }
}

function openSocket(code, conn) {
  // Don't bother opening a socket once the pool entry is being torn down.
  if (conn.destroyed) return;
  try {
    const ws = new WebSocket(getWsUrl(code));
    conn.ws = ws;
    ws.onopen = () => {
      conn.connected = true;
      conn.reconnectDelay = RECONNECT_START_MS;
      stopPolling(conn);
    };
    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        // Server heartbeat — silent, no state churn.
        if (parsed && parsed.type === 'ping') return;
        // Merge instead of replace — warming payloads omit `entries` and
        // we don't want to blank the initial HTTP fetch.
        conn.lastData = { ...(conn.lastData || {}), ...parsed };
        broadcast(conn, parsed);
      } catch (e) { logger.warn('WS message parse error:', e?.message); }
    };
    ws.onclose = () => {
      conn.connected = false;
      conn.ws = null;
      // Reconnect only if we still have subscribers OR are in the grace
      // window (i.e. likely to get one back). Otherwise stay quiet and
      // let the close-timer take us out.
      if (conn.destroyed || conn.isCompleted) return;
      if (conn.subscribers.size === 0 && !conn.closeTimer) return;
      startPolling(conn);
      conn.reconnectTimer = setTimeout(() => {
        conn.reconnectTimer = null;
        openSocket(code, conn);
      }, conn.reconnectDelay);
      conn.reconnectDelay = Math.min(conn.reconnectDelay * 2, RECONNECT_MAX_MS);
    };
    ws.onerror = () => {
      // ws.close() can throw if the socket is already in CLOSING/CLOSED —
      // intentionally swallow. The matching onclose handler is what actually
      // drives the reconnect logic; this is just a best-effort tidy-up.
      try { ws.close(); } catch (err) { logger.warn('WS close-on-error noop:', err?.message); }
    };
  } catch (e) {
    logger.warn('WS connect failed, falling back to polling:', e?.message);
    if (!conn.isCompleted) startPolling(conn);
  }
}

function destroyConnection(code, conn) {
  conn.destroyed = true;
  stopPolling(conn);
  if (conn.reconnectTimer) { clearTimeout(conn.reconnectTimer); conn.reconnectTimer = null; }
  if (conn.closeTimer) { clearTimeout(conn.closeTimer); conn.closeTimer = null; }
  if (conn.ws) {
    // Same defensive close as ws.onerror — log instead of swallowing so
    // a misbehaving browser's exception is visible in our logger sink.
    try { conn.ws.close(); } catch (err) { logger.warn('WS close noop:', err?.message); }
    conn.ws = null;
  }
  pool.delete(code);
}

export function subscribe(code, { fetchFn, isCompleted = false, onUpdate }) {
  if (!code || !onUpdate) return () => {};

  let conn = pool.get(code);
  if (!conn) {
    conn = {
      code,
      ws: null,
      pollTimer: null,
      reconnectTimer: null,
      closeTimer: null,
      reconnectDelay: RECONNECT_START_MS,
      subscribers: new Set(),
      lastData: null,
      fetchFn,
      isCompleted,
      connected: false,
      destroyed: false,
    };
    pool.set(code, conn);
    // Fire-and-forget initial fetch — gives the first subscriber data
    // even before WS open. Subsequent subscribers get `lastData` directly.
    (async () => {
      try {
        const result = await fetchFn?.();
        if (!result || conn.destroyed) return;
        conn.lastData = { ...(conn.lastData || {}), ...result };
        broadcast(conn, conn.lastData);
      } catch (e) { logger.warn('Initial leaderboard fetch failed:', e?.message); }
    })();
    if (!isCompleted) openSocket(code, conn);
  } else {
    // Reuse — keep the latest `fetchFn` so callers whose closures changed
    // (e.g. SweatPage's `fetchLeaderboard` after a setRoom render) supply
    // fresh state to the polling fallback.
    conn.fetchFn = fetchFn;
    if (conn.closeTimer) { clearTimeout(conn.closeTimer); conn.closeTimer = null; }
  }

  conn.subscribers.add(onUpdate);

  // Replay last data synchronously so the new subscriber doesn't have
  // to wait for the next WS broadcast (which could be 60 s away).
  if (conn.lastData) {
    try { onUpdate(conn.lastData); } catch (e) { logger.warn('Subscriber replay threw:', e?.message); }
  }

  return function unsubscribe() {
    conn.subscribers.delete(onUpdate);
    if (conn.subscribers.size === 0 && !conn.closeTimer && !conn.destroyed) {
      conn.closeTimer = setTimeout(() => destroyConnection(code, conn), GRACE_PERIOD_MS);
    }
  };
}

// Test helper — close everything on signout/logout so the next user starts clean.
export function closeAllLeaderboardConnections() {
  for (const [code, conn] of pool) destroyConnection(code, conn);
}

// HMR / page unload cleanup
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', closeAllLeaderboardConnections);
}
